<template>
    <section
        :id="`station-locator-${id}`"
        ref="dsstationlocator"
        class="station-locator"
        :class="{
            [`pin-hover-active`]: gmapZoom > 7,
        }"
    >
        <SLHeader
            :filters="computedFilters"
            :selected="selectedFilters"
            @input="updateSearch"
            @select="selectFilter"
            @blur="trackEvent('search')"
        />
        <SLList
            :cities
            :stations="filteredStations"
            @select-station="selectStation"
            @set-map="setMapLocation"
            @toggle-open="value => desktopMenuOpen = value"
        />
        <SLFilters
            v-if="!hideFilters"
            :count="{
                filtered: filteredStations.length,
                total: computedStations.length,
            }"
            :filters="computedFilters"
            :selected="selectedFilters"
            @select="selectFilter"
        />
        <div class="my-location">
            <button
                class="user-location"
                @click="findUserLocation"
            >
                <DSFontIcon code="currentlocationon" icon-style="line" />
            </button>
        </div>
        <div class="zoom-controls">
            <button
                :aria-label="$t('zoomIn')"
                class="plus"
                @click="gmapZoom++"
            >+</button>
            <button
                :aria-label="$t('zoomOut')"
                class="minus"
                @click="gmapZoom--"
            >-</button>
        </div>
        <div
            class="map-container"
            :class="{
                'sidebar-open': desktopMenuOpen,
            }"
        >
            <GoogleMap
                ref="map"
                class="map"
                :api-key="gmapsApiKey"
                :center="gmapCenter"
                :zoom="gmapZoom"
                v-bind="gmapConfig"
                map-id="gmap-instance"
                @ready="initMap()"
                @zoom_changed="setZoom(event)"
            >
                <GoogleMapAdvancedMarker
                    v-if="userLocation"
                    :z-index="2"
                    :options="{
                        zIndex: 2,
                        position: {
                            ...userLocation,
                        },
                        content: userPin(),
                    }"
                />
                <GoogleMapMarkerCluster
                    :options="clusterRenderer()"
                >
                    <GoogleMapAdvancedMarker
                        v-for="marker, key in computedMarkers"
                        :key="`marker-${key}`"
                        :options="{
                            collisionBehavior: 'OPTIONAL_AND_HIDES_LOWER_PRIORITY',
                            position: { ...marker.position },
                            content: stationPin(marker),
                        }"
                        @click="selectStation(marker)"
                    />
                </GoogleMapMarkerCluster>
            </GoogleMap>
        </div>
    </section>
</template>
<script setup>
import { ref, computed, onMounted, watch, provide, useI18n, useRuntimeConfig, useNuxtApp, useRoute, useRouter, useAnalytics } from '#imports';
import { httpStatus } from '~dsc/utils/http-status';

const { $locator, $bp } = useNuxtApp();
const map = ref(null);
const config = useRuntimeConfig();
const { apikey: gmapsApiKey, stationPages } = { ...config.public.locator };
const route = useRoute();
const router = useRouter();
const selectedStation = ref({});
const { gtm } = useAnalytics();

provide('selectedStation', selectedStation);

const i18n = useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {},
        'fi-FI': {
            back: 'Takaisin',
            open24h: 'Avoinna 24h',
            helmisimpukka: 'HelmiSimpukka',
            zoomIn: 'Suurenna',
            zoomOut: 'Pienennä',
            openingHours: 'Aukioloajat',
            day: {
                'today': 'Tänään',
                'tomorrow': 'Huomenna',
                '1': 'Maanantai',
                '2': 'Tiistai',
                '3': 'Keskiviikko',
                '4': 'Torstai',
                '5': 'Perjantai',
                '6': 'Lauantai',
                '7': 'Sunnuntai',
            },
        },
        'sv-SE': {
            zoomIn: 'Zooma in',
            zoomOut: 'Zooma ut',
        },
        'no-NO': {
            zoomIn: 'Forstørr',
            zoomOut: 'Zoom ut',
        },
    },
});
const typeFilter = ref('');
const defaultLocation = {
    lat: 62,
    lng: 18,
    zoom: 5,
};
const desktopMenuOpen = ref(true);

const stationPin = marker => {
    if (import.meta.client) {
        const pin = document.createElement('div');
        pin.classList.add('station-pin');
        if (marker.isClosed) {
            pin.classList.add('maintenance');
        }
        const image = document.createElement('img');
        const title = document.createElement('span');
        title.classList.add('title');
        title.innerHTML = marker.name;
        image.src = resolveMarkerImage(marker.brand);
        pin.appendChild(image);
        pin.appendChild(title);

        return pin;
    }

    return null;
};

const userPin = () => {
    if (import.meta.client) {
        const pin = document.createElement('div');
        pin.classList.add('user-pin');

        return pin;
    }

    return null;
};

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: '',
    },
    contentType: {
        type: String,
        required: false,
        default: 'externalDatasource',
    },
    type: {
        type: String,
        required: false,
        default: 'stationlocator',
    },
    filters: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    data: {
        type: Array,
        required: false,
        default: () => ([]),
    },
});

const gmapCenter = ref({
    lat: 62,
    lng: 18,
});
const gmapConfig = ref({
    backgroundColor: '#fff',
    disableDefaultUI: true,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
    noClear: true,
    streetViewControl: false,
    gestureHandling: 'greedy',
    maxZoom: 13,
    minZoom: 5,
    clickableIcons: false,
    scrollwheel: false,
});
const gmapZoom = ref(5);
const selectedFilters = ref([]);
const mapReady = computed(() => {
    if (!map.value) return false;

    return map.value.ready;
});
const userLocation = ref(null);

const hideFilters = computed(() => props.filters.hideFilters);

if (route.query?.q) {
    selectedFilters.value = route.query.q.split('|').map(item => item.match(',') ? item.split(',') : item);
}

const computedFilters = computed(() => {
    if (typeof (props.filters) === 'object' && Object.keys(props.filters).length > 0) {
        return props.filters;
    }

    return $locator.filters();
});

const computedStations = computed(() => props.data.map(({ path, station }) => ({
    path,
    ...station,
    brand: station?.services?.includes('ploq') ? 'ploq' : station?.brand,
    fuels: [...station.fuels.map(fuel => fuel.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, ''))],
    stationAttributes: [
        ...station.services?.map(service => service.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, '')) || null,
        ...station.stationTypes.map(type => type.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, '')),
        ...station.fuels.map(fuel => fuel.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, '')),
        ...station.trucks.map(fuel => fuel.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, '')),
    ],
})));

const filteredStations = computed(() => {
    const noFiltersSelected = selectedFilters.value.length === 0;
    const noSearch = typeFilter.value === '';
    const fiServiceStationExcludes = ['veneasema', 'cbg', 'lbg'];
    const fiService24StationExcludes = ['veneasema', 'cbg', 'lbg'];
    const seShellStationExcludes = ['cng', 'lbg'];
    const locale = i18n.locale.value;

    const stationAttributesHasSelectedFilters = station => selectedFilters.value.flat()
        .every(filter => {
            if (locale === 'fi-FI') {
                if (filter === 'servicestation') {
                    return station.stationAttributes.includes('servicestation') && fiServiceStationExcludes.every(exclude => !station.stationAttributes.includes(exclude));
                } else if (filter === 'service24hours') {
                    return station.stationAttributes.includes('service24hours') && fiService24StationExcludes.every(exclude => !station.stationAttributes.includes(exclude));
                }
            } else if (locale === 'sv-SE') {
                if (filter === 'laddstation') {
                    return station.stationAttributes.includes('laddstation') && !station.name.toLowerCase().includes('laddning');
                } else if (filter === 'shell') {
                    return station.stationAttributes.includes('shell') && seShellStationExcludes.every(exclude => !station.stationAttributes.includes(exclude));
                }
            } else if (locale === 'no-NO') {
                if (filter === 'expressautomaticstation') {
                    return station.stationAttributes.includes('expressautomaticstation') && !station.name.toLowerCase().includes(' crt ');
                }
            }

            return Object.values(station.stationAttributes).includes(filter);
        });

    const stationNameMatchesSearchQuery = station =>
        station.name.toLowerCase().includes(typeFilter.value.toLowerCase())
        || station.fullAddress?.toLowerCase().includes(typeFilter.value.toLowerCase());

    const filteredStations = (noFiltersSelected && noSearch)
        ? computedStations.value
        : computedStations.value.filter(station => !noSearch
            ? stationNameMatchesSearchQuery(station)
            : !noFiltersSelected
                ? stationAttributesHasSelectedFilters(station)
                : station);

    return filteredStations.sort((a, b) => a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1);
});

const computedMarkers = computed(() => {
    if (filteredStations.value.length > 0) {
        return filteredStations.value.filter(station =>
            (!isNaN(Number(station.location?.lat)) && !isNaN(Number(station.location?.lon))) && (Number(station.location?.lat) !== 0 && Number(station.location?.lon) !== 0),
        ).map(station => ({
            id: station.id,
            name: station.name,
            brand: station.brand,
            isClosed: station.isClosed,
            description: getDescription(station.stationAttributes),
            position: {
                lat: Number(station.location?.lat),
                lng: Number(station.location?.lon),
            },
            path: station.path,
        }));
    }

    return [];
});

watch(computedMarkers, to => {
    if (!map.value) {
        return;
    }
    const markerBounds = new map.value.api.LatLngBounds();

    if (to.length > 0) {
        to.forEach(item => {
            markerBounds.extend(new map.value.api.LatLng(item.position));
        });

        if (!map.value) return false;

        map.value.map.fitBounds(markerBounds);
    }
});

watch(() => JSON.stringify($bp.bps), () => {
    if ($bp.bps.isSmallDesktop) {
        desktopMenuOpen.value = true;
    } else {
        desktopMenuOpen.value = false;
    }

    const { height } = window.document.querySelector('header').getBoundingClientRect();
    if (height) {
        containerMinHeight.value = `calc(100vh - ${height}px)`;
    }
});

watch(mapReady, () => {
    if (!map.value) return false;

    const markerBounds = new map.value.api.LatLngBounds();

    if (computedMarkers.value.length > 0) {
        computedMarkers.value.forEach(item => {
            markerBounds.extend(new map.value.api.LatLng(item.position));
        });

        if (!map.value) return false;

        map.value.map.fitBounds(markerBounds);
    }

    if (selectedFilters.value.length === 0 && Object.keys(computedFilters.value).length > 0) {
        Object.values(computedFilters.value)
            .forEach(group => {
                if (typeof (group.filters) === 'object') {
                    group.filters.filter(filter => filter.selected)
                        .forEach(item => {
                            selectedFilters.value.push(item.code);
                        });
                }
            });
    }
});

const cities = ref([]);
const containerMinHeight = ref('100vh');

onMounted(() => {
    const { height } = window.document.querySelector('header').getBoundingClientRect();
    if (height) {
        containerMinHeight.value = `calc(100vh - ${height}px)`;
    }
    findUserLocation();
});

function findUserLocation() {
    if (import.meta.client && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
            const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            };

            userLocation.value = pos;
        });
    }
}

function getDescription(stationAttributes) {
    if (Object.values(stationAttributes)
        .includes('helmisimpukka')) {
        return i18n.t('helmisimpukka');
    } else if (Object.values(stationAttributes)
        .includes('helmisimpukkaexpress')) {
        return i18n.t('helmisimpukkaexpress');
    }

    return '';
}

async function searchCities(search) {
    const { places } = await map.value.api;
    cities.value = [];
    const autoComplete = new places.AutocompleteService();
    autoComplete.getPlacePredictions({
        input: search,
        componentRestrictions: { country: [ 'fi', 'se', 'no' ] },
        types: [ '(cities)' ],
    }, res => {
        cities.value = res?.map(city => ({
            placeId: city.place_id,
            title: city.structured_formatting?.main_text,
        }))
        .splice(0, 3) ?? [];
    });
}

function selectStation(station) {
    trackEvent('view', {
        station_id: station.id,
        station_name: station.name,
    });

    if (stationPages) {
        router.push(`/${station.path}`);

        return false;
    }

    const { lat, lon: lng } = { ...station.location };

    setMapLocation({
        lat: Number(lat),
        lng: Number(lng),
        zoom: 11,
    });

    selectedStation.value = computedStations.value.find(listStation => listStation.id === station.id);
}

function setMapLocation(target) {
    if (target.lat && target.lng) {
        gmapCenter.value = { lat: target.lat, lng: target.lng };
    }
    if (target.zoom) {
        gmapZoom.value = target.zoom;
    }
}

function setZoom() {
    const zoomLevel = map.value.map.zoom;

    if (zoomLevel) {
        gmapZoom.value = map.value.map.zoom;
    } else {
        gmapZoom.value = 5;
    }
}

function resolveMarkerImage(brand) {
    const lcBrand = brand.toLowerCase();
    if (lcBrand === 'ploq') {
        return '/images/ploq-logo.png';
    } else if (lcBrand === 'shell') {
        return '/images/shell-logo.png';
    }

    return '/images/st1-logo.webp';
}

function clusterRenderer() {
    if (!map.value || !import.meta.client) {
        return {};
    }

    return {
        renderer: {
            render({ count, position }) {
                return new map.value.api.Marker({
                    label: {
                        text: String(count),
                        color: '#000',
                        fontWeight: 'bold',
                    },
                    position,
                    icon: {
                        url: '/images/cluster-pin.png',
                    },
                });
            },
        },
    };
}

function updateSearch(value) {
    typeFilter.value = value;

    if (value === '') {
        cities.value = [];
        setMapLocation(defaultLocation);

        return;
    }

    searchCities(value);
}

function selectFilter(value) {
    const { value: filters } = selectedFilters;

    const formattedFilters = filters.map(item => JSON.stringify(item));
    const formattedValue = JSON.stringify(value);
    let filterState = 'off';

    if (formattedFilters.includes(formattedValue)) {
        const index = formattedFilters.indexOf(formattedValue);
        filters.splice(index, 1);
    } else if (filters.includes(value)) {
        filters.splice(filters.indexOf(value), 1);
    } else {
        filterState = 'on';
        filters.push(value);
    }

    trackEvent('filter', {
        station_search: {
            filters: value,
            filter_state: filterState,
        },
    });

    updatePathParams();
}

function updatePathParams() {
    const { path } = route;
    const { value: filters } = selectedFilters;

    if (filters.length > 0) {
        history.pushState({}, document.title, `${path}?q=${filters.join('|')}`);

        return;
    }
    history.pushState({}, document.title, `${path}`);
}

function trackEvent(type, payload) {
    const commonAttributes = {
        search_term: typeFilter.value.length > 0 ? encodeURIComponent(typeFilter.value.toLowerCase()) : 'no search term',
        station_search: {
            filters: selectedFilters.value.length ? selectedFilters.value.join('|') : 'unfiltered',
        },
    };

    switch (type) {
        case 'search':
            if (filteredStations.value.length === 0) {
                trackEvent('empty');
            } else {
                gtm().push({
                    event: 'station_search',
                    ...commonAttributes,
                });
            }
        break;

        case 'filter':
            if (typeFilter.value) {
                trackEvent('search');
            }

            gtm().push({
                event: 'station_search_filter',
                ...commonAttributes,
                ...payload,
            });
        break;

        case 'view':
            gtm().push({
                event: 'station_view',
                ...payload,
            });
        break;

        case 'empty':
            gtm().push({
                event: 'struggle',
                ...commonAttributes,
                gtm: {
                    error_status: httpStatus.NOT_FOUND,
                    error_code: 'failed.station.search',
                    error_text: 'station search with zero results',
                    error_type: 'struggle',
                },
            });
        break;
    }
}
</script>
<style>
.station-locator .map-container .gm-style > :nth-child(16) > div {
  transition: transform 0.5s ease;
}
.station-locator .map-container.sidebar-open .gm-style > :nth-child(16) > div {
  transform: translateX(-12rem);
}
.station-locator .station-pin {
  padding: 0.25rem;
  border-radius: 0.25rem;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
  background: var(--color-base-white);
  font-weight: 600;
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;
}
.station-locator .station-pin img {
  width: 1.25rem;
  height: 1.25rem;
  object-fit: contain;
}
.station-locator .station-pin .title {
  position: absolute;
  margin-left: 0;
  transition: all 0.5s ease;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
  left: 1.625rem;
  width: auto;
  height: 1.75rem;
  padding: 0 0.25rem;
  border-radius: 0 0.25rem 0.25rem 0;
  display: inline-flex;
  align-items: center;
}
.station-locator .station-pin::after {
  content: "";
  position: absolute;
  left: 0.875rem;
  bottom: 0;
  width: 0.5rem;
  height: 0.5rem;
  transform: translate(-50%, 50%) rotateZ(45deg);
  background: linear-gradient(-45deg, var(--color-base-white) 0 50%, transparent 50% 100%);
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.2);
}
.station-locator .station-pin.maintenance img {
  filter: grayscale(100%);
}
.station-locator .user-pin {
  position: relative;
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  background: var(--color-base-white);
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
}
.station-locator .user-pin::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-information-400);
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.station-locator.pin-hover-active .station-pin:hover .title {
  background: var(--color-base-white);
  opacity: 1;
}
</style>
<style lang="scss" scoped>
@import '../../assets/scss/mixins';

.station-locator {
    transition: grid-template-columns .5s ease;
    display: grid;
    grid-template-rows: auto 1fr;
    min-height: v-bind(containerMinHeight);
    height: v-bind(containerMinHeight);
    will-change: grid-template-columns;
    position: relative;

    @include respond-to('at least desktop') {
        grid-template-columns: 21.25rem 1fr;
    }

    .search-header {
        grid-column-start: 1;
        grid-row-start: 1;
    }

    .filters-list {
        grid-column-start: 1;
        grid-row-start: 2;
        max-height: 100%;
        overflow-y: scroll;

        @include respond-to('at most small desktop') {
            grid-row-start: 1;
            grid-row-end: span 2;
        }
    }

    .station-list {
        grid-column-start: 1;
        grid-row-start: 1;
        grid-row-end: span 2;
        max-height: 100%;
    }

    .map-container {
        transition: transform .5s ease;
        grid-column-start: 2;
        grid-row-start: 1;
        grid-row-end: span 2;

        @include respond-to('at least desktop') {
            &.sidebar-open {
                transform: translateX(12rem);
            }
        }
    }

    @include respond-to('at most small desktop') {
        grid-template-columns: 100%;
        overflow-y: visible;

        .search-header > :last-child {
            margin-bottom: 0;
        }

        .map-container {
            height: 100%;
            grid-column-start: 1;
            grid-row-start: 2;
            grid-row-end: span 1;
        }
    }
}

.my-location {
    position: absolute;
    grid-column-start: 2;
    grid-row-start: 1;
    grid-row-end: span 2;
    right: 0;
    bottom: 1.5rem;
    z-index: 4;
    width: calc(100% - 12rem);
    pointer-events: none;
    display: flex;
    flex-flow: column wrap;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 1rem 1rem 0 0;

    button {
        background: var(--color-base-white);
        display: inline-flex;
        width: auto;
        padding: .5rem;
        border-radius: 50%;
        box-shadow: 0 2px 2px rgba(0, 0, 0, .2);
        pointer-events: auto;
    }

    @include respond-to('at most small desktop') {
        bottom: 5rem;
        right: 1.625rem;
        align-items: flex-start;
    }
}

.zoom-controls {
    position: absolute;
    grid-column-start: 2;
    grid-row-start: 1;
    grid-row-end: span 2;
    right: 0;
    top: 0;
    z-index: 4;
    width: calc(100% - 12rem);
    pointer-events: none;
    display: flex;
    flex-flow: column wrap;
    justify-content: flex-end;
    padding: 1rem 1rem 0 0;

    button {
        margin-left: auto;
        width: 2rem;
        background: var(--color-base-white);
        border: 1px solid var(--color-base-grey-200);
        padding: .25rem .5rem;
        font-weight: 800;
        pointer-events: auto;
        box-shadow: 0 2px 2px rgba(0, 0, 0, .2);

        &.plus {
            border-radius: .25rem .25rem 0 0;
        }

        &.minus {
            border-top-width: 0;
            border-radius: 0 0 .25rem .25rem;
        }
    }

    @include respond-to('at most small desktop') {
        top: auto;
        bottom: .5rem;
        right: 1.5rem;
    }
}

.map-container {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 0;

    .map {
        position: absolute;
        width: 100%;
        height: 100%;

        > .custom-marker-wrapper {
            display: none;
        }
    }
}

.city-suggestions {
    background-color: var(--color-base-white);

    .city-suggestion {
        padding: 1rem;
        margin-bottom: .5rem;
        background-color: var(--color-base-white);
        border: 1px solid var(--color-base-grey-200);
        cursor: pointer;

        &:hover {
            background-color: var(--color-secondary-500);
        }
    }
}
</style>
